import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { ApolloLink, InMemoryCache } from '@apollo/client/core';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import {
  provideTranslateCompiler,
  provideTranslateService,
  TranslateService,
} from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideApollo } from 'apollo-angular';
import { createClient, Client as GqlWsClient } from 'graphql-ws';
import {
  AutoRefreshTokenService,
  createInterceptorCondition,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  IncludeBearerTokenCondition,
  includeBearerTokenInterceptor,
  provideKeycloak,
  UserActivityService,
  withAutoRefreshToken,
} from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { it } from 'primelocale/it.json';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { environment } from '../environments/environment';
import { langInterceptor } from './_core/_interceptor/lang.interceptor';
import { InitService } from './_core/_services/init.service';
import { j4g } from './_core/_theme/j4g.theme';
import { routes } from './app.routes';
import { Kind, OperationTypeNode } from 'graphql';
import { HttpLink } from 'apollo-angular/http';
import { FORMLY_CONFIG, provideFormlyCore } from '@ngx-formly/core';
import { AccordionFieldType } from './_core/_formly/_types/accordion.type';
import { AddressFieldType } from './_core/_formly/_types/address.type';
import { ButtonFieldType } from './_core/_formly/_types/button.type';
import { CharacteristicsFieldType } from './_core/_formly/_types/characteristics.type';
import { CheckboxFieldType } from './_core/_formly/_types/checkbox.type';
import { DatepickerFieldType } from './_core/_formly/_types/datepicker.type';
import { DocumentFieldType } from './_core/_formly/_types/document.type';
import { EditorFieldType } from './_core/_formly/_types/editor.type';
import { MapFieldType } from './_core/_formly/_types/map.type';
import { MultiSelectFieldType } from './_core/_formly/_types/multiselect.type';
import { NumberFieldType } from './_core/_formly/_types/number.type';
import { PasswordFieldType } from './_core/_formly/_types/password.type';
import { RadioFieldType } from './_core/_formly/_types/radio.type';
import { RepeaterAccordionFieldType } from './_core/_formly/_types/repeater-accordion.type';
import { RepeaterFieldType } from './_core/_formly/_types/repeater.type';
import { SelectButtonFieldType } from './_core/_formly/_types/select-button.type';
import { SelectFieldType } from './_core/_formly/_types/select.type';
import { StepperFieldType } from './_core/_formly/_types/stepper.type';
import { TabFieldType } from './_core/_formly/_types/tab.type';
import { TemplateFieldType } from './_core/_formly/_types/template.type';
import { TextFieldType } from './_core/_formly/_types/text.type';
import { TextAreaFieldType } from './_core/_formly/_types/textarea.type';
import { ToggleFieldType } from './_core/_formly/_types/toggle.type';
import { CardWrapperFormField } from './_core/_formly/_wrappers/card.wrapper';
import { CheckboxFormlyWrapper } from './_core/_formly/_wrappers/checkbox.wrapper';
import { DrawerWrapperFormField } from './_core/_formly/_wrappers/drawer.wrapper';
import { EditableFormlyWrapper } from './_core/_formly/_wrappers/editable.wrapper';
import { FormlyWrapperFormField } from './_core/_formly/_wrappers/form-field.wrapper';
import { SearchFormlyWrapper } from './_core/_formly/_wrappers/search.wrapper';
import { ContattoValidator } from './_core/_formly/validation/contatto.validation';
import { phoneNumberValidator } from './_core/_formly/validation/phone-number-validation';
import {
  podValidator,
  podValidatorGSE,
} from './_core/_formly/validation/pod.validation';
import { TaxIdCodeValidator } from './_core/_formly/validation/tax-id-code-validation';
import { vatNumberValidator } from './_core/_formly/validation/vat-number-validation';
import { EmailValidator } from './_core/_formly/validation/email-validation';
import { withFormlyPrimeNG } from '@ngx-formly/primeng';

const urlCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
  urlPattern: /^(?:https:\/\/(?:[^/]+\.)?join4green\.com\/.*)$/,
  bearerPrefix: 'Bearer',
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(() => inject(InitService).init()),
    MessageService,
    ConfirmationService,
    provideAnimations(),
    providePrimeNG({
      translation: it,
      ripple: true,
      theme: {
        preset: j4g,
        options: {
          darkModeSelector: '.light',
          cssLayer: {
            name: 'primeng',
            order: 'base, components, primeng, utilities',
          },
        },
      },
    }),
    provideTranslateService({
      fallbackLang: 'it',
      loader: provideTranslateHttpLoader({
        prefix: './i18n/',
        suffix: '.json',
      }),
      compiler: provideTranslateCompiler(TranslateMessageFormatCompiler),
    }),

    provideKeycloak({
      config: environment.keycloak,
      initOptions: {
        onLoad: 'check-sso',
        checkLoginIframe: false,
        silentCheckSsoRedirectUri:
          window.location.origin + '/silent-check-sso.html',
      },
      features: [
        withAutoRefreshToken({
          sessionTimeout: 900000,
          onInactivityTimeout: 'logout',
        }),
      ],
      providers: [
        AutoRefreshTokenService,
        UserActivityService,
        {
          provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
          useValue: [urlCondition], // <-- Note that multiple conditions might be added.
        },
      ],
    }),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      }),
    ),

    provideHttpClient(
      withInterceptors([includeBearerTokenInterceptor, langInterceptor]),
    ),
    provideApollo(() => {
      const keycloak = inject(Keycloak);
      const httpLinkHandler = inject(HttpLink).create({
        uri: environment.hasura.url,
      });
      const httpLink = new ApolloLink(
        (operation) => httpLinkHandler.request(operation)!,
      );
      const defaultWsLink = createWsLinkForRole('anonymous', keycloak);

      const link = ApolloLink.split(
        // Split based on operation type
        ({ query }) => {
          const definition = getMainDefinition(query);
          return (
            definition.kind === Kind.OPERATION_DEFINITION &&
            definition.operation === OperationTypeNode.SUBSCRIPTION
          );
        },
        defaultWsLink,
        httpLink,
      );

      return {
        link,
        cache: new InMemoryCache(),
        defaultOptions: {
          watchQuery: {
            fetchPolicy: 'cache-and-network', // o 'network-only', a seconda della tua esigenza
            errorPolicy: 'ignore',
          },
          query: {
            fetchPolicy: 'network-only', // <-- qui imposti per query come la tua
            errorPolicy: 'all',
          },
        },
      };
    }),
    provideStore(),
    provideEffects(),
    provideRouterStore(),
    {
      provide: FORMLY_CONFIG,
      multi: true,
      useFactory: validationConfig,
      deps: [TranslateService],
    },
    provideFormlyCore([
      ...withFormlyPrimeNG(),
      {
        types: [
          { name: 'repeater', component: RepeaterFieldType, wrappers: [] },
          { name: 'stepper', component: StepperFieldType, wrappers: [] },
          { name: 'accordion', component: AccordionFieldType, wrappers: [] },
          {
            name: 'address',
            component: AddressFieldType,
            wrappers: ['wrapper'],
          },
          {
            name: 'multiselect',
            component: MultiSelectFieldType,
            wrappers: ['wrapper'],
          },
          {
            name: 'select',
            component: SelectFieldType,
            wrappers: ['wrapper'],
          },
          {
            name: 'textarea',
            component: TextAreaFieldType,
            wrappers: ['wrapper'],
          },
          {
            name: 'editor',
            component: EditorFieldType,
            wrappers: ['wrapper'],
          },
          {
            name: 'button',
            component: ButtonFieldType,
          },
          {
            name: 'datepicker',
            component: DatepickerFieldType,
            wrappers: ['wrapper'],
          },
          {
            name: 'text',
            component: TextFieldType,
            wrappers: ['wrapper'],
          },
          {
            name: 'password',
            component: PasswordFieldType,
            wrappers: ['wrapper'],
          },
          {
            name: 'toggle',
            component: ToggleFieldType,
            wrappers: ['checkbox'],
          },
          {
            name: 'select-button',
            component: SelectButtonFieldType,
            wrappers: ['checkbox'],
          },
          {
            name: 'radio',
            component: RadioFieldType,
            wrappers: ['checkbox'],
          },
          {
            name: 'checkbox',
            component: CheckboxFieldType,
            wrappers: ['checkbox'],
          },
          {
            name: 'number',
            component: NumberFieldType,
            wrappers: ['wrapper'],
          },
          {
            name: 'document',
            component: DocumentFieldType,
            wrappers: [],
          },
          {
            name: 'map',
            component: MapFieldType,
            wrappers: ['wrapper'],
          },
          {
            name: 'characteristics',
            component: CharacteristicsFieldType,
            wrappers: ['wrapper'],
          },
          { name: 'tab', component: TabFieldType, wrappers: [] },
          { name: 'template', component: TemplateFieldType, wrappers: [] },
          {
            name: 'repeater-accordion',
            component: RepeaterAccordionFieldType,
            wrappers: [],
          },
        ],
        wrappers: [
          {
            name: 'wrapper',
            component: FormlyWrapperFormField,
          },
          {
            name: 'checkbox',
            component: CheckboxFormlyWrapper,
          },
          {
            name: 'card',
            component: CardWrapperFormField,
          },
          {
            name: 'drawer',
            component: DrawerWrapperFormField,
          },
          {
            name: 'search',
            component: SearchFormlyWrapper,
          },
          {
            name: 'editable',
            component: EditableFormlyWrapper,
          },
        ],
        validators: [
          {
            name: 'email',
            validation: EmailValidator,
          },
          {
            name: 'contatto',
            validation: ContattoValidator,
          },
          {
            name: 'taxIdCode',
            validation: TaxIdCodeValidator,
          },
          {
            name: 'vatNumber',
            validation: vatNumberValidator,
          },
          {
            name: 'pod',
            validation: podValidator,
          },
          {
            name: 'podGSE',
            validation: podValidatorGSE,
          },
          {
            name: 'phoneNumber',
            validation: phoneNumberValidator,
          },
        ],
      },
    ]),
    provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }),
    { provide: LOCALE_ID, useValue: 'it-IT' },
  ],
};
export function createWsLinkForRole(
  role: string,
  keycloak: Keycloak,
): GraphQLWsLink {
  return new GraphQLWsLink(
    createClient({
      url: environment.hasura.wss,
      retryAttempts: Infinity,
      shouldRetry: () => true,
      keepAlive: 10000,
      connectionParams: async () => {
        const token = await keycloak.token;
        return {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'x-hasura-lang': localStorage.getItem('lang') ?? 'it',
            'x-hasura-role': role,
          },
        };
      },
    }),
  );
}

function validationConfig(translate: TranslateService) {
  return {
    validationMessages: [
      {
        name: 'percentage',
        message() {
          return translate.stream('formly.validation.percentage');
        },
      },
      {
        name: 'percentage_val',
        message() {
          return translate.stream('formly.validation.percentage_val');
        },
      },
      {
        name: 'percentage_base',
        message() {
          return translate.stream('formly.validation.percentage_base');
        },
      },
      {
        name: 'percentage_eccedenza',
        message() {
          return translate.stream('formly.validation.percentage_eccedenza');
        },
      },
      {
        name: 'required',
        message() {
          return translate.stream('formly.validation.required');
        },
      },
      {
        name: 'taxIdCode',
        message() {
          return translate.stream('formly.validation.taxIdCode');
        },
      },
      {
        name: 'contatto',
        message() {
          return translate.stream('formly.validation.contatto');
        },
      },
      {
        name: 'email',
        message() {
          return translate.stream('formly.validation.email');
        },
      },
      {
        name: 'vatNumber',
        message() {
          return translate.stream('formly.validation.vatNumber');
        },
      },
      {
        name: 'pod',
        message() {
          return translate.stream('formly.validation.pod');
        },
      },
      {
        name: 'podGSE',
        message() {
          return translate.stream('formly.validation.podGSE');
        },
      },
      {
        name: 'podGSECheck',
        message() {
          return translate.stream('formly.validation.podGSECheck');
        },
      },
      {
        name: 'phoneNumber',
        message() {
          return translate.stream('formly.validation.phoneNumber');
        },
      },
      {
        name: 'referral',
        message() {
          return translate.stream('formly.validation.referral');
        },
      },
      {
        name: 'uniqueReferral',
        message() {
          return translate.stream('formly.validation.uniqueReferral');
        },
      },
      {
        name: 'minLength',
        message(field: any) {
          return translate.stream('formly.validation.minLength', {
            value: field.requiredLength,
          });
        },
      },
    ],
  };
}
